// --- React
import React from "react";
// --- UI Components
import Modal from "@dtci/spellbook/Modal";
import Typography from "@dtci/spellbook/Typography";
// --- Hooks
import { useDialogsActions } from "./hooks";
import { useDialogsForm } from "./forms";
// --- Styles
import styles from "./dialog.module.scss";

/**
 * Header Component
 *
 * This component renders the header of the dialog.
 *
 * @component
 * @param {string} title - The title of the dialog.
 * @param {string} icon - The icon class name to be displayed.
 * * @returns {JSX.Element} The rendered Header component.
 */
const Header = ({ title, icon }) => (
  <div className={styles.header}>
    <i className={`${styles.headerIcon} far fa-xl ${icon}`} />
    <Typography variant="T50" bold className={`${styles.title} louis-typo`}>
      {title}
    </Typography>
  </div>
);

/**
 * Dialog Component
 *
 * This component renders a modal dialog with a title, content, and action buttons.
 * It uses the Modal component from the Spellbook library.
 *
 * @component
 * @param {string} uid - The unique identifier for the dialog.
 * @param {string} title - The title of the dialog.
 * @param {string|JSX.Element} content - The content to be displayed in the dialog.
 * @param {boolean} closeAction - Whether to show the close button.
 * @param {Object} primaryAction - The primary action button configuration.
 * @param {Object} secondaryAction - The secondary action button configuration.
 * @param {boolean} hideClose - Whether to hide the close button.
 * @param {boolean} isDismissable - Whether the dialog is dismissible.
 * * @returns {JSX.Element} The rendered Dialog component.
 */
export const Dialog = ({
  uid,
  title = "",
  content,
  closeAction = false,
  primaryAction = {},
  secondaryAction = {},
  hideClose = false,
  isDismissable = true,
}) => {
  const { closeDialog } = useDialogsActions();
  const form = useDialogsForm();

  const primaryBtn =
    Object.keys(primaryAction).length > 0
      ? {
          primaryButton: primaryAction.label,
          primaryButtonProps: {
            disabled: primaryAction.disabled,
          },
          primaryButtonAction: primaryAction.action,
        }
      : {};

  let secondaryBtn =
    Object.keys(secondaryAction).length > 0
      ? {
          secondaryButton: secondaryAction.label,
          secondaryButtonProps: {
            disabled: secondaryAction.disabled,
          },
          secondaryButtonAction: secondaryAction.action,
        }
      : {};

  const handleClose = (event) => {
    if (event !== undefined) {
      form.reset();
      event.stopPropagation();
      if (isDismissable) closeDialog();
    }
  };

  if (closeAction === true) {
    secondaryBtn = {
      secondaryButton: "Close",
      secondaryButtonAction: handleClose,
    };
  }
  return (
    <Modal
      bodyOverlay="blackout"
      closeOnClickOutside={isDismissable}
      closeOnEscape={isDismissable}
      dialogClassName={styles.modal}
      headerText={<Header title={title} />}
      hideClose={hideClose}
      modalId={uid}
      onClose={handleClose}
      smallButtons
      {...primaryBtn}
      {...secondaryBtn}
    >
      {typeof content === "string" ? (
        <Typography variant="T20">{content}</Typography>
      ) : (
        content
      )}
    </Modal>
  );
};
